import { computed, signal } from "@preact/signals";

import LatestRequest from "LatestRequest";
import readlangClient from "readlangClient";

export interface TagData {
  name: string;
  count: number;
}

interface TagMetadata {
  name: string;
  displayName: string;
  icon?: string;
  alwaysVisible?: boolean;
}

const TAG_METADATA: TagMetadata[] = [
  {
    name: "all",
    displayName: "All",
    alwaysVisible: true,
  },
  {
    name: "queue",
    displayName: "To Read",
    icon: "book",
    alwaysVisible: true,
  },
  {
    name: "completed",
    displayName: "Completed",
    icon: "trophy",
  },
  {
    name: "abandoned",
    displayName: "Archived",
    icon: "archive",
  },
  {
    name: "shared",
    displayName: "Shared",
    icon: "share",
  },
  {
    name: "deleted",
    displayName: "Deleted",
    icon: "trash",
  },
];

const VISIBLE_LIBRARY_TAGS = ["queue", "completed", "abandoned", "shared"];

const inVisibleLibrary = (tags: string[]) =>
  VISIBLE_LIBRARY_TAGS.some((tagName) => tags.includes(tagName));

type TagView = TagMetadata & {
  count: number;
  showCount: boolean;
  active: boolean;
  visible: boolean;
  value: string;
};

export default class BookTagsStore {
  readonly tags = signal<TagData[]>([]);
  private readonly latestFetch = new LatestRequest();
  readonly activeTag = signal<string | null>("queue");
  readonly viewJSON = computed<{ tags: TagView[] }>(() => {
    const tags = TAG_METADATA.map((meta) => {
      const tagData = this.tags.value.find((tag) => tag.name === meta.name);
      const count = tagData?.count ?? 0;
      const showCount = !!tagData;
      return {
        ...meta,
        count,
        showCount,
        active: meta.name === this.activeTag.value,
        visible: meta.alwaysVisible || showCount,
        value: meta.name,
      };
    });

    return { tags };
  });

  async fetch({ language }: { language: string }) {
    const response = await this.latestFetch.send(
      readlangClient.request("GET", "/api/bookTags", { language })
    );
    if (response) {
      this.tags.value = response.data;
    }
  }

  addTags(tagsToAdd: string[], previousTags: string[]) {
    const deltas: Record<string, number> = {};
    const nextTags = Array.from(new Set([...previousTags, ...tagsToAdd]));
    for (const tagName of tagsToAdd) {
      deltas[tagName] = (deltas[tagName] ?? 0) + 1;
    }

    if (!inVisibleLibrary(previousTags) && inVisibleLibrary(nextTags)) {
      deltas.all = (deltas.all ?? 0) + 1;
      deltas.deleted = (deltas.deleted ?? 0) - 1;
    }

    this.applyCountDeltas(deltas);
  }

  removeTags(tagsToRemove: string[], remainingTags: string[]) {
    const deltas: Record<string, number> = {};
    const previousTags = Array.from(
      new Set([...remainingTags, ...tagsToRemove])
    );
    for (const tagName of tagsToRemove) {
      deltas[tagName] = (deltas[tagName] ?? 0) - 1;
    }

    if (inVisibleLibrary(previousTags) && !inVisibleLibrary(remainingTags)) {
      deltas.all = (deltas.all ?? 0) - 1;
      deltas.deleted = (deltas.deleted ?? 0) + 1;
    }

    this.applyCountDeltas(deltas);
  }

  removeDeletedBook() {
    this.applyCountDeltas({ deleted: -1 });
  }

  setTagCount(name: string, count: number) {
    this.tags.value = this.tags.value.map((tag) =>
      tag.name === name ? { ...tag, count } : tag
    );
  }

  private applyCountDeltas(deltas: Record<string, number>) {
    const existingNames = new Set(this.tags.value.map((tag) => tag.name));
    const updated = this.tags.value.map((tag) => ({
      ...tag,
      count: tag.count + (deltas[tag.name] ?? 0),
    }));

    for (const [name, delta] of Object.entries(deltas)) {
      if (!existingNames.has(name)) {
        updated.push({ name, count: delta });
      }
    }

    this.tags.value = updated;
  }
}
